import { extractBands } from './bands'
import { applyBrightness, blendTrail, createColorSampler } from './color'
import { EFFECT_RENDERERS } from './effects'
import type {
  AudioBands,
  AudioRhythmColorId,
  AudioRhythmMode,
  AudioRhythmSettings,
  Rgb,
  VisualKey,
} from './types'

/** 频谱柱列能量每帧向目标靠近的比例（平滑柱长变化，减轻真机分段刷新可见度）。 */
const COLUMN_SMOOTH = 0.7

export interface AudioRhythmEngineOptions {
  keys: VisualKey[]
  rowCount: number
  columnCount: number
  /** 主灯 → 副灯映射（空格等宽键作为整体键亮灭）。 */
  boundAuxLedMap: Map<number, number[]>
}

export interface AudioRhythmRenderResult {
  /** 本帧灯效帧：ledIndex → RGB（已含拖尾、亮度、副灯映射、静音淡出）。 */
  frame: Map<number, Rgb>
  /** 本帧频段快照（供 UI 显示与信号判定）。 */
  bands: AudioBands
  /** 是否检测到音效信号。 */
  hasSignal: boolean
}

/**
 * 音频律动灯效引擎（纯计算，无 DOM / Vue 依赖）。
 *
 * 职责：把 AnalyserNode 的频域数据 + 灯效设置，渲染为本帧的 ledIndex → RGB 映射。
 * 内部维护列频谱平滑、颜色采样缓存、拖尾、亮度、静音淡出、副灯映射等跨帧状态。
 * 「灯效计算实现」全部收口于此；设备下发由 deviceLightSink 独立承担。
 */
export class AudioRhythmEngine {
  private readonly keys: VisualKey[]
  private readonly rowCount: number
  private readonly columnCount: number
  private readonly boundAuxLedMap: Map<number, number[]>

  private prevLow = 0
  private prevColumns: number[] | null = null
  private prevFrame = new Map<number, Rgb>()
  private lastActiveFrame = new Map<number, Rgb>()
  private silentSince = 0

  private cachedColorId: AudioRhythmColorId | null = null
  private cachedSampleColor: ((t: number) => Rgb) | null = null

  constructor(options: AudioRhythmEngineOptions) {
    this.keys = options.keys
    this.rowCount = options.rowCount
    this.columnCount = options.columnCount
    this.boundAuxLedMap = options.boundAuxLedMap
  }

  renderFrame(
    frequencyData: Uint8Array,
    sampleRate: number,
    mode: AudioRhythmMode,
    colorId: AudioRhythmColorId,
    settings: AudioRhythmSettings,
    timeSec: number,
  ): AudioRhythmRenderResult {
    const bands = extractBands(frequencyData, sampleRate, settings, this.prevLow, timeSec)
    this.prevLow = bands.low

    // 列频谱指数平滑：柱长若随音频突变，会产生大片键同时亮灭，
    // 真机 0xdd 分段下发时会暴露分片刷新；限制每帧柱长变化量后伸缩更顺滑。
    if (!this.prevColumns) this.prevColumns = bands.columns.slice()
    for (let i = 0; i < bands.columns.length; i++) {
      const prev = this.prevColumns[i] ?? 0
      this.prevColumns[i] = prev + ((bands.columns[i] ?? 0) - prev) * COLUMN_SMOOTH
    }

    // 复用颜色采样器（colorId 不变时避免每帧 find + 重建闭包）
    if (this.cachedColorId !== colorId || !this.cachedSampleColor) {
      this.cachedColorId = colorId
      this.cachedSampleColor = createColorSampler(colorId)
    }
    const sampleColor = this.cachedSampleColor
    const renderer = EFFECT_RENDERERS[mode]
    // 直接复用 bands 对象（columns 原地平滑），避免每帧 spread 新对象
    bands.columns = this.prevColumns
    const raw = renderer({
      keys: this.keys,
      bands,
      sampleColor,
      columnCount: this.columnCount,
      rowCount: this.rowCount,
    })

    // 拖尾保留率：扫描线按「无拖尾」设计，引擎级混合会留残影、
    // 真机低帧率下尾部 50%→25%→12% 阶梯残影观感偏亮且拉长成多排；
    // 扫描线彻底关闭拖尾（其余模式跟随用户设置）。
    const trailSmoothing = mode === 'scanLine' ? 0 : settings.trailSmoothing

    // 拖尾
    const trailed = new Map<number, Rgb>()
    for (const key of this.keys) {
      const curr = raw.get(key.ledIndex) ?? { r: 0, g: 0, b: 0 }
      const blended = blendTrail(this.prevFrame.get(key.ledIndex), curr, trailSmoothing)
      if (blended.r + blended.g + blended.b > 2) {
        trailed.set(key.ledIndex, blended)
      }
    }
    // 拖尾衰减：上一帧有、本帧无的键也要衰减
    for (const [idx, prev] of this.prevFrame) {
      if (trailed.has(idx)) continue
      const faded = blendTrail(prev, { r: 0, g: 0, b: 0 }, trailSmoothing)
      if (faded.r + faded.g + faded.b > 2) trailed.set(idx, faded)
    }

    let bright = applyBrightness(trailed, settings.brightness)
    this.prevFrame = bright

    // 音效信号检测：频谱能量高于底噪才算真实音频。
    const hasSignal =
      bands.low > 0.01 ||
      bands.mid > 0.01 ||
      bands.high > 0.01 ||
      bands.overall > 0.01 ||
      bands.columns.some((c) => c > 0.01)

    if (hasSignal) {
      this.lastActiveFrame = bright
      this.silentSince = 0
    } else if (!this.silentSince) {
      this.silentSince = timeSec
    }

    // 静音淡出：无信号超过 0.5s 后，把最后有效画面在 0.6s 内渐变到黑。
    const silentSec = this.silentSince ? timeSec - this.silentSince : 0
    const fade = silentSec > 0.5 ? Math.max(0, 1 - (silentSec - 0.5) / 0.6) : 1
    if (fade < 1) {
      bright = new Map()
      for (const [idx, c] of this.lastActiveFrame) {
        const r = Math.round(c.r * fade)
        const g = Math.round(c.g * fade)
        const b = Math.round(c.b * fade)
        if (r + g + b > 2) bright.set(idx, { r, g, b })
      }
    }

    // 空格等宽键副灯：颜色保持各自位置的采样，但亮度对齐主灯——整体一起亮灭。
    // 位置型移动效果除外：扫描线会让空格 5 颗灯珠整体同亮同灭、
    // 视觉上像整条空格一次点亮；波浪的波脊沿 nx 起伏、火焰的边界沿灯位分布，
    // 都会因对齐产生「整条空格同闪」或「火焰外灯珠被强制点亮」。
    // 这三种模式跳过对齐，每颗副灯按自身位置被效果驱动。
    if (mode !== 'scanLine' && mode !== 'wave' && mode !== 'flame') {
      for (const [main, extras] of this.boundAuxLedMap) {
        const mainColor = bright.get(main)
        for (const e of extras) {
          const own = bright.get(e)
          if (!mainColor) {
            bright.delete(e)
          } else if (!own) {
            bright.set(e, mainColor)
          } else {
            const scale =
              (mainColor.r + mainColor.g + mainColor.b) / Math.max(1, own.r + own.g + own.b)
            bright.set(e, {
              r: Math.min(255, own.r * scale),
              g: Math.min(255, own.g * scale),
              b: Math.min(255, own.b * scale),
            })
          }
        }
      }
    }

    return { frame: bright, bands, hasSignal }
  }

  reset(): void {
    this.prevLow = 0
    this.prevColumns = null
    this.prevFrame = new Map()
    this.lastActiveFrame = new Map()
    this.silentSince = 0
  }
}
